import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    Row,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from "reactstrap";
import classNames from "classnames";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import withRouter from "components/Common/withRouter";
import Loaders from "components/Common/Loaders";
import { urlEndPoint } from "helpers/GeneralHelper";
import { normalizeMetadataFilterDateForApi } from "helpers/metadataFilterHelpers";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import {
    addTaskPlanner,
    addTaskPlannerComment,
    assignTaskPlanner,
    searchTaskPlannerAssigned,
    deleteTaskPlanner,
    getTaskPlannerById,
    getTaskPlannerForEditById,
    getTaskPlannerCommentsByTaskId,
    getTaskPlannerStatusOptions,
    getTaskPlannerPriorityOptions,
    removeTaskPlannerAssigned,
    submitTaskPlannerFilterData,
    updateTaskPlanner,
} from "model/TaskPlannerModel";
import { getDefaultUserList } from "model/UserModel";
import { toast } from "react-toastify";
import { countActiveMetadataFilters } from "helpers/metadataFilterHelpers";

/* =======================
   STATIC ARRAYS
======================= */

const INITIAL_TASK_FORM = {
    id: "",
    title: "",
    description: "",
    assignedTo: "",
    dueDate: "",
    priority: "medium",
    taskBucketId: "",
    status: "not started",
};

const STATUS_OPTIONS = [
    { value: "completed", label: "Completed" },
    { value: "in progress", label: "In Progress" },
    { value: "not started", label: "Not Started" },
];

const DEFAULT_TASK_PLANNER_FILTER = {
    nameContains: "",
    statusId: "",
    fromDate: null,
    toDate: null,
    selectAE: "",
    priorities: [],
};

/* =======================
   COMPONENT
======================= */

const mapTaskPlannerSelectOptionsFromApi = (payload) => {
    const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.records)
                ? payload.records
                : [];
    return list
        .map((item) => {
            const value = String(item?.value ?? item?.id ?? item?.code ?? "").trim();
            const label = String(
                item?.label ?? item?.data ?? item?.name ?? item?.title ?? item?.value ?? ""
            ).trim();
            if (!value) return null;
            return { value, label: label || value };
        })
        .filter(Boolean);
};

const formatTaskPlannerCardDate = (value) => {
    if (!value) return "-";
    const parsed = dayjs(value);
    if (parsed.isValid()) return parsed.format("MM-DD-YYYY");
    return String(value).slice(0, 10) || "-";
};

const toDateOnlyInput = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
    return String(value).slice(0, 10) || "";
};

const buildTaskDueDatePayload = (date, time = "00:00") => {
    const datePart = String(date || "").trim();
    if (!datePart) return "";
    const timePart = String(time || "00:00").trim() || "00:00";
    const normalizedTime = /^\d{2}:\d{2}:\d{2}$/.test(timePart)
        ? timePart
        : `${timePart}:00`;
    return `${datePart}T${normalizedTime}`;
};

const resolveTaskCardAssignees = (task = {}) => {
    const otherAssignees = Array.isArray(task?.otherAssignees) ? task.otherAssignees : [];
    if (otherAssignees.length) return otherAssignees;
    return [
        ...(Array.isArray(task?.accountExecutiveAssignees) ? task.accountExecutiveAssignees : []),
        ...(Array.isArray(task?.userTypeAssignees) ? task.userTypeAssignees : []),
    ];
};

const mapAllAssignedUsersToMentionOptions = (users = []) => {
    const list = Array.isArray(users) ? users : [];
    return list
        .map((item) => {
            const value = String(item?.id ?? "").trim();
            const label = String(item?.name ?? "").trim();
            if (!value || !label) return null;
            return { value, label };
        })
        .filter(Boolean);
};

const mapAssignedSearchOptionsFromApi = (payload = {}) => {
    const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
            ? payload.data
            : [];
    return list
        .map((item) => {
            const isUserType = Boolean(item?.isUserType);
            const userOrUserTypeId = String(
                isUserType ? item?.userTypeId ?? "" : item?.userId ?? ""
            ).trim();
            const label = String(item?.displayName ?? "").trim();
            const assigneeKey = String(item?.assigneeKey ?? "").trim() || null;
            if (!userOrUserTypeId || !label) return null;
            return {
                value: userOrUserTypeId,
                label,
                assigneeKey,
                isUserType,
            };
        })
        .filter(Boolean);
};

const formatAccountExecutiveMentionToken = (rawName = "") => {
    const sanitizedName = String(rawName || "")
        .trim()
        .replace(/\s+/g, " ")
        .replace(/[^a-zA-Z0-9 _-]/g, "");
    return sanitizedName ? `@${sanitizedName}` : "@";
};

const getActiveCommentMentionContext = (text = "", cursorPosition = 0) => {
    const normalizedCursor = Number.isFinite(cursorPosition)
        ? Math.max(0, Math.min(cursorPosition, String(text).length))
        : String(text).length;
    const textBeforeCursor = String(text).slice(0, normalizedCursor);
    const mentionStartIndex = textBeforeCursor.lastIndexOf("@");
    if (mentionStartIndex < 0) return null;

    const charBeforeMention = mentionStartIndex > 0 ? textBeforeCursor[mentionStartIndex - 1] : "";
    if (charBeforeMention && !/\s/.test(charBeforeMention)) return null;

    const mentionQuery = textBeforeCursor.slice(mentionStartIndex + 1);
    if (/\s/.test(mentionQuery)) return null;

    return { mentionStartIndex, mentionQuery };
};

const getNativeTextControlElement = (node) => {
    if (!node) return null;
    if (typeof node.setSelectionRange === "function") return node;
    if (node.input && typeof node.input.setSelectionRange === "function") return node.input;
    if (typeof node.querySelector === "function") {
        const nestedTextarea = node.querySelector("textarea");
        if (nestedTextarea && typeof nestedTextarea.setSelectionRange === "function") {
            return nestedTextarea;
        }
    }
    return null;
};

const TASK_COMMENT_MENTION_PATTERN = /@([A-Za-z0-9]+(?:[\s-][A-Za-z0-9]+)?)/g;
const TASK_COMMENT_URL_PATTERN =
    /(https?:\/\/[^\s<]+[^\s<.,;:!?"')\]}>]|www\.[^\s<]+[^\s<.,;:!?"')\]}>])/gi;

const stripTaskCommentMentionsForSubmit = (text = "") =>
    String(text || "").replace(TASK_COMMENT_MENTION_PATTERN, "$1");

const escapeTaskCommentHtml = (value = "") =>
    String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const preserveTaskCommentNewlines = (htmlText = "") =>
    String(htmlText || "").replace(/\r\n|\r|\n/g, "<br>");

const linkifyTaskCommentUrls = (htmlText = "") =>
    String(htmlText || "").replace(TASK_COMMENT_URL_PATTERN, (rawUrl, offset, fullText) => {
        const textBeforeMatch = String(fullText || "").slice(0, offset);
        const lastTagOpenIndex = textBeforeMatch.lastIndexOf("<");
        const lastTagCloseIndex = textBeforeMatch.lastIndexOf(">");
        // Skip URLs that already sit inside an HTML tag attribute (href/src/etc).
        if (lastTagOpenIndex > lastTagCloseIndex) return rawUrl;

        const openAnchorCount = (textBeforeMatch.match(/<a\b/gi) || []).length;
        const closeAnchorCount = (textBeforeMatch.match(/<\/a>/gi) || []).length;
        // Skip URLs that are already wrapped in an anchor.
        if (openAnchorCount > closeAnchorCount) return rawUrl;

        const href = /^www\./i.test(rawUrl) ? `https://${rawUrl}` : rawUrl;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="task-comment-link">${rawUrl}</a>`;
    });

const sanitizeTaskCommentHtmlForDisplay = (html = "") => {
    const normalizedHtml = String(html || "");
    if (!normalizedHtml) return "";

    return normalizedHtml.replace(
        /<a\b([^>]*?)>([\s\S]*?)<\/a>/gi,
        (fullMatch, attributes, innerHtml) => {
            const hasValueAttr = /\bvalue\s*=\s*(["'])[\s\S]*?\1/i.test(attributes);
            if (!hasValueAttr) return fullMatch;

            const displayText = String(innerHtml || "").trim();
            return `<span class="task-comment-mention-token" style="color:#0d1b6e;font-weight:600">${displayText}</span>`;
        }
    );
};

const formatTaskCommentMentionHtml = (rawText = "") => {
    const normalizedText = String(rawText || "");
    if (!normalizedText) return "";
    if (normalizedText.includes("task-comment-mention-token")) return normalizedText;

    const escapedText = escapeTaskCommentHtml(normalizedText);
    return escapedText.replace(
        TASK_COMMENT_MENTION_PATTERN,
        (mentionToken) =>
            `<span class="task-comment-mention-token" style="color:#0d1b6e">${mentionToken}</span>`
    );
};

const formatTaskCommentMentionDisplayHtml = (rawText = "") => {
    const normalizedText = String(rawText || "");
    if (!normalizedText) return "";
    if (normalizedText.includes("task-comment-mention-token")) {
        return preserveTaskCommentNewlines(linkifyTaskCommentUrls(normalizedText));
    }

    if (/<[a-z][\s\S]*>/i.test(normalizedText)) {
        const hasValueMentionAnchor = /<a\b[^>]*\bvalue\s*=/i.test(normalizedText);
        const displayHtml = hasValueMentionAnchor
            ? sanitizeTaskCommentHtmlForDisplay(normalizedText)
            : normalizedText.replace(
                  TASK_COMMENT_MENTION_PATTERN,
                  (mentionToken) =>
                      `<span class="task-comment-mention-token" style="color:#0d1b6e;font-weight:600">${mentionToken}</span>`
              );
        return preserveTaskCommentNewlines(linkifyTaskCommentUrls(displayHtml));
    }

    const escapedText = escapeTaskCommentHtml(normalizedText);
    const withLinks = linkifyTaskCommentUrls(escapedText);
    const withMentions = withLinks.replace(
        TASK_COMMENT_MENTION_PATTERN,
        (mentionToken) =>
            `<span class="task-comment-mention-token" style="color:#0d1b6e;font-weight:600">${mentionToken}</span>`
    );
    return preserveTaskCommentNewlines(withMentions);
};

const getDownloadUrl = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
    const baseUrl = String(process.env.REACT_APP_LINK_URL || "").replace(/\/+$/, "");
    const linkPath = raw.replace(/^\/+/, "");
    return baseUrl ? `${baseUrl}/${linkPath}` : `/${linkPath}`;
};

const ReviewImageDownloadLink = ({ href, className = "" }) => {
    const downloadUrl = getDownloadUrl(href);
    if (!downloadUrl) return null;

    return (
        <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            download
            className={`client-review-download-link mt-2 ${className}`.trim()}
        >
            <i className="mdi mdi-download" aria-hidden="true" />
            <span>Download image</span>
        </a>
    );
};

const focusTextControlAtPosition = (node, position = 0) => {
    const textControl = getNativeTextControlElement(node);
    if (!textControl || typeof textControl.setSelectionRange !== "function") return;
    const normalizedPosition = Math.max(
        0,
        Math.min(position, String(textControl.value || "").length)
    );
    textControl.focus();
    textControl.setSelectionRange(normalizedPosition, normalizedPosition);
};

const getTaskCardDescriptionPreview = (rawText = "", maxLength = 72) => {
    const plainText = String(rawText || "")
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (!plainText) return "—";
    if (plainText.length <= maxLength) return plainText;
    return `${plainText.slice(0, maxLength)}...`;
};

const mapTaskFromApi = (task = {}) => {
    const assignees = resolveTaskCardAssignees(task);
    const primaryAssigneeName =
        task?.assignedToName ||
        null;

    return {
        id: task?.id || "",
        title: task?.title || "",
        priority: task?.priority || "medium",
        status: task?.status || "",
        start: formatTaskPlannerCardDate(task?.createdDate),
        due: formatTaskPlannerCardDate(task?.dueDate),
        owner: primaryAssigneeName,
        assignedToName: task?.assignedToName || primaryAssigneeName,
        comments: Number(task?.commentCount || 0),
        taskBucketId: task?.taskBucketId || "",
        referenceId: task?.referenceId || "",
        description: task?.description || "",
        bucketTitle: task?.bucketTitle || "",
        otherAssigneeCount: Number(task?.otherAssigneeCount ?? assignees.length ?? 0),
        otherAssignees: assignees,
    };
};

const TaskPlanner = ({ router }) => {
    const gridRef = useRef(null);
    const assignedPickerRef = useRef(null);
    const taskCommentInputRef = useRef(null);
    const taskCommentBackdropRef = useRef(null);
    const taskCommentCursorRef = useRef(0);
    const commentMentionRef = useRef(null);
    const [plannerBuckets, setPlannerBuckets] = useState([]);
    const [plannerRows, setPlannerRows] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [taskModalMode, setTaskModalMode] = useState("view");
    const [taskModalLoading, setTaskModalLoading] = useState(false);
    const [taskForm, setTaskForm] = useState(INITIAL_TASK_FORM);
    const [userOptions, setUserOptions] = useState([]);
    const [searchUserOptions, setSearchUserOptions] = useState([]);
    const [assignedItems, setAssignedItems] = useState([]);
    const [assignedPickerOpen, setAssignedPickerOpen] = useState(false);
    const [assignedSearch, setAssignedSearch] = useState("");
    const [assigningUserId, setAssigningUserId] = useState("");
    const [taskComment, setTaskComment] = useState("");
    const [taskMentionOptions, setTaskMentionOptions] = useState([]);
    const [commentMentionOpen, setCommentMentionOpen] = useState(false);
    const [commentMentionQuery, setCommentMentionQuery] = useState("");
    const [commentMentionLoading, setCommentMentionLoading] = useState(false);
    const [commentMentionHighlightIndex, setCommentMentionHighlightIndex] = useState(0);
    const [taskImageFile, setTaskImageFile] = useState(null);
    const [taskComments, setTaskComments] = useState([]);
    const [taskCommentsLoading, setTaskCommentsLoading] = useState(false);
    const [taskCommentSaving, setTaskCommentSaving] = useState(false);
    const [imagePreviewModalOpen, setImagePreviewModalOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState("");
    const [previewImageAlt, setPreviewImageAlt] = useState("Comment image");
    const [taskStatusOptions, setTaskStatusOptions] = useState([]);
    const [taskPriorityOptions, setTaskPriorityOptions] = useState([]);
    const [taskPlannerFilter, setTaskPlannerFilter] = useState(DEFAULT_TASK_PLANNER_FILTER);
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
    const [focusedBucketId, setFocusedBucketId] = useState(null);
    const userData = localStorage.getItem("userData");


    const handleBucketHeaderClick = (bucketId = "") => {
        const id = String(bucketId || "");
        if (!id) return;
        setFocusedBucketId((prev) => (String(prev) === id ? null : id));
    };

    const scrollPlannerBoard = (direction) => {
        const scrollContainer = gridRef.current;
        if (!scrollContainer) return;

        const columnStep = 372;
        const step = Math.max(columnStep, Math.floor(scrollContainer.clientWidth * 0.8));
        scrollContainer.scrollBy({
            left: direction * step,
            behavior: "smooth",
        });
    };

    const scrollLeft = () => scrollPlannerBoard(-1);
    const scrollRight = () => scrollPlannerBoard(1);

    const buildTaskPlannerFilterPayload = (rawFilter = {}) => {
        return {
            nameContains: rawFilter?.nameContains || "",
            statusId: rawFilter?.statusId || "",
            fromDate: normalizeMetadataFilterDateForApi(rawFilter?.fromDate),
            toDate: normalizeMetadataFilterDateForApi(rawFilter?.toDate),
            selectAE: rawFilter?.selectAE || "",
            priorities: Array.isArray(rawFilter?.priorities) ? rawFilter.priorities : [],
        };
    };

    const fetchTaskPlannerTasks = async (payload = null) => {
        setTasksLoading(true);
        try {
            const requestPayload = payload || buildTaskPlannerFilterPayload(taskPlannerFilter);
            const response = await submitTaskPlannerFilterData(requestPayload);
            const responseData = response?.data || {};
            setPlannerBuckets(Array.isArray(responseData?.buckets) ? responseData.buckets : []);
            setPlannerRows(Array.isArray(responseData?.rows) ? responseData.rows : []);
        } finally {
            setTasksLoading(false);
        }
    };

    const taskPlannerFilterModalData = useMemo(
        () => [
            {
                title: "Search by Title",
                field: "nameContains",
                type: "text",
                placeholder: "Search by Title",
                value: taskPlannerFilter?.nameContains || "",
            },
            {
                title: "Select Status",
                field: "statusId",
                type: "select",
                itemArray: STATUS_OPTIONS.map((item) => ({
                    value: item.value,
                    label: item.label,
                })),
                value: taskPlannerFilter?.statusId || "",
            },
            {
                title: "From Date",
                field: "fromDate",
                type: "date",
                value: taskPlannerFilter?.fromDate || null,
            },
            {
                title: "To Date",
                field: "toDate",
                type: "date",
                value: taskPlannerFilter?.toDate || null,
            },
            {
                title: "Select AE",
                field: "selectAE",
                type: "dropdown",
                api: "get_account_executive_users",
                placeholder: "Select AE",
                value: taskPlannerFilter?.selectAE || "",
            },
            {
                title: "Select Priority",
                field: "priorities",
                type: "multiselect",
                itemArray: taskPriorityOptions.map((item) => ({
                    value: item.value,
                    label: item.label,
                })),
                value: taskPlannerFilter?.priorities || [],
            },
        ],
        [taskPlannerFilter, taskPriorityOptions]
    );

    const activeTaskPlannerFilterCount = useMemo(
        () => countActiveMetadataFilters(taskPlannerFilter, taskPlannerFilterModalData),
        [taskPlannerFilter, taskPlannerFilterModalData]
    );

    const handleTaskPlannerFilterSubmit = async (formData = {}) => {
        const priorities = Array.isArray(formData?.priorities)
            ? formData.priorities
            : String(formData?.priorities || "")
                  .split(",")
                  .map((item) => item.trim().toLowerCase())
                  .filter(Boolean);

        const nextFilter = {
            nameContains: formData?.nameContains || "",
            statusId: formData?.statusId || "",
            fromDate: formData?.fromDate || null,
            toDate: formData?.toDate || null,
            selectAE: formData?.selectAE || "",
            priorities,
        };
        setTaskPlannerFilter(nextFilter);
        setCommonFilterModalOpen(false);
        await fetchTaskPlannerTasks(buildTaskPlannerFilterPayload(nextFilter));
    };

    const handleTaskPlannerFilterReset = async () => {
        const resetFilter = { ...DEFAULT_TASK_PLANNER_FILTER };
        setTaskPlannerFilter(resetFilter);
        setCommonFilterModalOpen(false);
        await fetchTaskPlannerTasks(buildTaskPlannerFilterPayload(resetFilter));
    };

    const fetchUserOptions = async () => {
        const response = await getDefaultUserList({});
        const records = response?.data?.records || response?.data || [];
        const getUserLabel = (item) => {
            const fullName = `${item?.firstName || ""} ${item?.lastName || ""}`.trim();
            return (
                fullName ||
                item?.userName ||
                item?.name ||
                item?.userEmail ||
                item?.email ||
                "User"
            );
        };
        const mapped = (Array.isArray(records) ? records : [])
            .map((item) => ({
                value: item?.id || item?._id || item?.userId || "",
                label: getUserLabel(item),
            }))
            .filter((item) => item?.value);
        setUserOptions(mapped);
    };

    const parseAssignedToItems = (assignedToRaw = "") => {
        const assignedValues = Array.isArray(assignedToRaw)
            ? assignedToRaw
            : String(assignedToRaw || "")
                  .split(",")
                  .map((item) => item.trim())
                  .filter(Boolean);

        return assignedValues.map((value) => {
            const matchedById = userOptions.find((option) => String(option?.value) === String(value));
            if (matchedById) {
                return { id: matchedById.value, label: matchedById.label };
            }
            const matchedByLabel = userOptions.find(
                (option) => String(option?.label || "").toLowerCase().trim() === String(value).toLowerCase().trim()
            );
            if (matchedByLabel) {
                return { id: matchedByLabel.value, label: matchedByLabel.label };
            }
            return { id: value, label: value };
        });
    };

    const buildAssignedItemsFromDtoList = (assignedDTOList = []) => {
        const rows = Array.isArray(assignedDTOList) ? assignedDTOList : [];
        const seenPrincipalIds = new Set();
        const normalized = [];

        rows.forEach((item) => {
            const principalId = item?.userId || item?.userTypeId || "";
            const assignmentId = String(item?.id ?? "").trim();
            if (!principalId || !assignmentId) return;
            if (seenPrincipalIds.has(String(principalId))) return;
            seenPrincipalIds.add(String(principalId));

            const label =
                item?.userName ||
                item?.userTypeName ||
                userOptions.find((option) => String(option?.value) === String(principalId))?.label ||
                principalId;

            normalized.push({
                assignmentId,
                id: principalId,
                label,
            });
        });

        return normalized;
    };

    const refreshAssignedItemsFromTask = async (taskId = "") => {
        const resolvedTaskId = String(taskId || "").trim();
        if (!resolvedTaskId) return;
        const response = await getTaskPlannerForEditById(resolvedTaskId);
        const details = response?.data || {};
        const assignedFromDto = buildAssignedItemsFromDtoList(details?.assignedDTOList || []);
        setAssignedItems(assignedFromDto);
        syncAssignedToField(assignedFromDto);
        setTaskMentionOptions([]);
    };

    const syncAssignedToField = (items = []) => {
        const ids = (items || []).map((item) => item?.id).filter(Boolean);
        setTaskForm((prev) => ({
            ...prev,
            assignedTo: ids.join(","),
        }));
    };

    const normalizeCommentRows = (rows = []) => {
        const list = Array.isArray(rows) ? rows : [];
        const apiLinkBase = String(process.env.REACT_APP_LINK_URL || "").replace(/\/+$/, "");
        const buildImageUrl = (rawLink = "") => {
            const link = String(rawLink || "").trim();
            if (!link) return "";
            if (/^https?:\/\//i.test(link)) return link;
            if (!apiLinkBase) return link;
            return `${apiLinkBase}/${link.replace(/^\/+/, "")}`;
        };
        return list.map((item, idx) => {
            const fullName = `${item?.firstName || ""} ${item?.lastName || ""}`.trim();
            const mappedUserName =
                userOptions.find((option) => String(option?.value) === String(item?.commentBy || ""))?.label || "";
            return {
                id: item?.id || item?.commentId || `comment-${idx}`,
                userName:
                    fullName ||
                    item?.userName ||
                    item?.commentByName ||
                    mappedUserName ||
                    item?.createdByName ||
                    "User",
                commentText: item?.commentText || item?.comment || item?.text || "",
                imageUrl: buildImageUrl(
                    item?.fileLink ||
                        item?.imageUrl ||
                        item?.imageFileUrl ||
                        item?.imageFile ||
                        item?.image ||
                        item?.fileUrl ||
                        ""
                ),
                createdOn:
                    item?.commentDate ||
                    item?.createdOn ||
                    item?.createdDate ||
                    item?.commentOn ||
                    item?.updatedOn ||
                    "",
            };
        });
    };

    const fetchTaskComments = async (taskId = "") => {
        if (!taskId) {
            setTaskComments([]);
            return;
        }
        setTaskCommentsLoading(true);
        const response = await getTaskPlannerCommentsByTaskId(taskId);
        const responseData = response?.data || {};
        const records =
            responseData?.records ||
            responseData?.data ||
            responseData?.comments ||
            responseData ||
            [];
        setTaskComments(normalizeCommentRows(records));
        setTaskCommentsLoading(false);
    };

    const resetCommentMentionState = () => {
        setCommentMentionOpen(false);
        setCommentMentionQuery("");
        setCommentMentionHighlightIndex(0);
        setCommentMentionLoading(false);
    };

    const openImagePreview = (url, alt = "Comment image") => {
        const resolved = getDownloadUrl(url);
        if (!resolved) return;
        setPreviewImageAlt(alt);
        setPreviewImageUrl(resolved);
        setImagePreviewModalOpen(true);
    };

    const closeImagePreview = () => {
        setImagePreviewModalOpen(false);
        setPreviewImageUrl("");
        setPreviewImageAlt("Comment image");
    };

    const handleCommentTextImageActivate = (event, commentItem = {}) => {
        const target = event?.target;
        if (!target || String(target.tagName || "").toUpperCase() !== "IMG") return;

        const imageSrc = target.getAttribute("src") || target.src || "";
        if (!imageSrc) return;

        if (event.type === "keydown") {
            event.preventDefault();
        }

        openImagePreview(
            imageSrc,
            target.getAttribute("alt") || `Comment by ${commentItem?.userName || "User"}`
        );
    };

    const loadTaskMentionOptions = async () => {
        if (taskMentionOptions.length) return taskMentionOptions;

        const taskId = String(taskForm?.id || "").trim();
        if (!taskId) {
            toast.error("Please save task first");
            return [];
        }

        setCommentMentionLoading(true);
        try {
            const response = await getTaskPlannerById(taskId);
            const options = mapAllAssignedUsersToMentionOptions(response?.data?.allAssignedUsers);
            setTaskMentionOptions(options);
            return options;
        } catch (error) {
            toast.error(error?.message || "Unable to load assigned users");
            return [];
        } finally {
            setCommentMentionLoading(false);
        }
    };

    const filteredCommentMentionOptions = useMemo(() => {
        const normalizedQuery = String(commentMentionQuery || "").trim().toLowerCase();
        const options = Array.isArray(taskMentionOptions) ? taskMentionOptions : [];
        if (!normalizedQuery) return options;
        return options.filter((option) =>
            String(option?.label || "")
                .toLowerCase()
                .includes(normalizedQuery)
        );
    }, [taskMentionOptions, commentMentionQuery]);

    const insertCommentMention = (selectedOption = {}) => {
        const textControl = getNativeTextControlElement(taskCommentInputRef.current);
        const currentText = String(taskComment || "");
        const cursorPosition =
            textControl?.selectionStart ??
            taskCommentCursorRef.current ??
            currentText.length;
        const mentionContext = getActiveCommentMentionContext(currentText, cursorPosition);
        if (!mentionContext) {
            resetCommentMentionState();
            return;
        }

        const mentionToken = formatAccountExecutiveMentionToken(selectedOption?.label);
        const textBeforeMention = currentText.slice(0, mentionContext.mentionStartIndex);
        const textAfterCursor = currentText.slice(cursorPosition);
        const nextCommentText = `${textBeforeMention}${mentionToken} ${textAfterCursor}`;
        const nextCursorPosition = textBeforeMention.length + mentionToken.length + 1;

        setTaskComment(nextCommentText);
        taskCommentCursorRef.current = nextCursorPosition;
        resetCommentMentionState();

        requestAnimationFrame(() => {
            focusTextControlAtPosition(taskCommentInputRef.current, nextCursorPosition);
        });
    };

    const handleTaskCommentChange = (event) => {
        const nextValue = event?.target?.value || "";
        const cursorPosition = event?.target?.selectionStart ?? nextValue.length;
        taskCommentCursorRef.current = cursorPosition;
        setTaskComment(nextValue);

        const mentionContext = getActiveCommentMentionContext(nextValue, cursorPosition);
        if (!mentionContext) {
            resetCommentMentionState();
            return;
        }

        setCommentMentionQuery(mentionContext.mentionQuery);
        setCommentMentionOpen(true);
        setCommentMentionHighlightIndex(0);
        void loadTaskMentionOptions();
    };

    const syncTaskCommentBackdropScroll = (event) => {
        if (taskCommentBackdropRef.current) {
            taskCommentBackdropRef.current.scrollTop = event?.target?.scrollTop || 0;
        }
    };

    const focusTaskCommentInput = () => {
        taskCommentInputRef.current?.focus();
    };

    const handleTaskCommentKeyDown = (event) => {
        if (!commentMentionOpen) return;

        const visibleMentionOptions = filteredCommentMentionOptions;
        if (!visibleMentionOptions.length) {
            if (event.key === "Escape") {
                event.preventDefault();
                resetCommentMentionState();
            }
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setCommentMentionHighlightIndex((previousIndex) =>
                Math.min(previousIndex + 1, visibleMentionOptions.length - 1)
            );
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            setCommentMentionHighlightIndex((previousIndex) =>
                Math.max(previousIndex - 1, 0)
            );
            return;
        }

        if (event.key === "Enter" || event.key === "Tab") {
            event.preventDefault();
            insertCommentMention(
                visibleMentionOptions[commentMentionHighlightIndex] || visibleMentionOptions[0]
            );
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            resetCommentMentionState();
        }
    };

    const openTaskModal = async (mode, task = null) => {
        setTaskModalMode(mode);
        setTaskComment("");
        setTaskMentionOptions([]);
        resetCommentMentionState();
        setTaskImageFile(null);
        setTaskCommentSaving(false);
        setAssignedPickerOpen(false);
        setAssignedSearch("");
        setSearchUserOptions([]);
        setAssigningUserId("");
        setTaskComments([]);
        setTaskCommentsLoading(false);
        if (!task?.id) {
            setTaskForm(INITIAL_TASK_FORM);
            setAssignedItems([]);
            setTaskModalOpen(true);
            return;
        }
        setTaskModalLoading(true);
        setTaskModalOpen(true);
        try {
            const response = await getTaskPlannerForEditById(task?.id);
            const details = response?.data || {};
            const saveDTO = details?.saveDTO || details || {};
            const assignedFromDto = buildAssignedItemsFromDtoList(details?.assignedDTOList || []);
            const commentsFromDetails =
                details?.commentDTOList ||
                details?.comments ||
                saveDTO?.commentDTOList ||
                saveDTO?.comments ||
                [];
            const assignedToValue = (assignedFromDto || []).map((item) => item?.id).filter(Boolean).join(",");
            setTaskForm({
                id: saveDTO?.id || task?.id || "",
                title: saveDTO?.title || "",
                description: saveDTO?.description || "",
                assignedTo: assignedToValue || saveDTO?.assignedTo || "",
                dueDate: toDateOnlyInput(saveDTO?.dueDate || task?.dueDate || ""),
                priority: saveDTO?.priority || "medium",
                taskBucketId: saveDTO?.taskBucketId || task?.taskBucketId || "",
                status: saveDTO?.status || "not started",
            });
            setAssignedItems(
                (assignedFromDto || []).length
                    ? assignedFromDto
                    : parseAssignedToItems(saveDTO?.assignedTo || details?.assignedTo || "")
            );
            if ((commentsFromDetails || []).length) {
                setTaskComments(normalizeCommentRows(commentsFromDetails));
            } else {
                await fetchTaskComments(saveDTO?.id || task?.id || "");
            }
        } finally {
            setTaskModalLoading(false);
        }
    };

    const closeTaskModal = () => {
        setTaskModalOpen(false);
        setTaskModalLoading(false);
        setAssignedPickerOpen(false);
        setAssignedSearch("");
        setSearchUserOptions([]);
        setAssigningUserId("");
        setTaskComments([]);
        setTaskCommentsLoading(false);
        setTaskCommentSaving(false);
        setTaskMentionOptions([]);
        resetCommentMentionState();
        const taskIdFromRoute = String(router?.params?.taskId || "").trim();
        if (taskIdFromRoute) {
            router.navigate(`/${urlEndPoint.taskPlanner}`, { replace: true });
        }
    };

    const handleTaskFormChange = (e) => {
        const { name, value } = e.target;
        setTaskForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleTaskSave = async (e) => {
        e.preventDefault();
        const payload = {
            id: taskForm?.id || "",
            title: taskForm?.title || "",
            description: taskForm?.description || "",
            assignedTo: taskForm?.assignedTo || "",
            dueDate: buildTaskDueDatePayload(taskForm?.dueDate),
            priority: taskForm?.priority || "",
            taskBucketId: taskForm?.taskBucketId || "",
            status: taskForm?.status || "",
        };

        const response =
            taskModalMode === "edit" && payload?.id
                ? await updateTaskPlanner(payload.id, payload)
                : await addTaskPlanner(payload);

        if (response?.success || response?.isSuccess) {
            toast.success(response?.message || "Task saved successfully");
            closeTaskModal();
            fetchTaskPlannerTasks(buildTaskPlannerFilterPayload(taskPlannerFilter));
            return;
        }
        toast.error(response?.message || "Unable to save task");
    };

    const handleReferenceNavigate = (task = {}) => {
        const referenceId = String(task?.referenceId || "").trim();
        if (!referenceId) {
            toast.error("Reference not found for this task");
            return;
        }
        const referenceUrl = `/${urlEndPoint.addEditOnboarding}?id=${encodeURIComponent(referenceId)}`;
        window.open(referenceUrl, "_blank", "noopener,noreferrer");
    };

    const handleTaskDelete = async (task = {}) => {
        const taskId = task?.id || "";
        if (!taskId) return;

        const swalResult = await Swal.fire({
            title: "Delete task?",
            text: "Are you sure you want to delete this task?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete",
            cancelButtonText: "Cancel",
            reverseButtons: true,
            target: document.body,
            allowOutsideClick: false,
            didOpen: () => {
                const el = document.querySelector(".swal2-container");
                if (el) el.style.zIndex = 20000;
            },
        });
        if (!swalResult?.isConfirmed) return;

        const response = await deleteTaskPlanner(taskId);
        if (response?.success || response?.isSuccess) {
            toast.success(response?.message || "Task deleted successfully");
            fetchTaskPlannerTasks(buildTaskPlannerFilterPayload(taskPlannerFilter));
            return;
        }
        toast.error(response?.message || "Unable to delete task");
    };

    const handleRemoveAssigned = async (assignedItem = {}) => {
        const assignmentId = String(assignedItem?.assignmentId ?? "").trim();
        const principalId = String(assignedItem?.id ?? "").trim();
        if (!principalId && !assignmentId) return;

        const swalResult = await Swal.fire({
            title: "Remove assigned user?",
            text: "This user will be removed from this task.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, remove",
            cancelButtonText: "Cancel",
            reverseButtons: true,
            target: document.body,
            allowOutsideClick: false,
            didOpen: () => {
                const el = document.querySelector(".swal2-container");
                if (el) el.style.zIndex = 20000;
            },
        });
        if (!swalResult?.isConfirmed) return;

        if (!taskForm?.id) {
            const nextItems = assignedItems.filter((item) => String(item?.id) !== principalId);
            setAssignedItems(nextItems);
            syncAssignedToField(nextItems);
            return;
        }

        if (!assignmentId) {
            toast.error("Assignment id is missing");
            return;
        }

        const response = await removeTaskPlannerAssigned(assignmentId);
        if (response?.success || response?.isSuccess) {
            toast.success(response?.message || "Assigned user removed");
            await refreshAssignedItemsFromTask(taskForm?.id);
            return;
        }
        toast.error(response?.message || "Unable to remove assigned user");
    };

    const handleAssignUser = async (option = {}) => {
        const userOrUserTypeId = option?.value || "";
        if (!userOrUserTypeId) return;
        const alreadyAssigned = assignedItems.some((item) => String(item?.id) === String(userOrUserTypeId));
        if (alreadyAssigned) return;

        if (!taskForm?.id) {
            const nextItems = [...assignedItems, { id: userOrUserTypeId, label: option?.label || "User" }];
            setAssignedItems(nextItems);
            syncAssignedToField(nextItems);
            setAssignedSearch("");
            return;
        }

        setAssigningUserId(userOrUserTypeId);
        const response = await assignTaskPlanner({
            taskPlannerId: taskForm?.id || "",
            assigneeKey: option?.assigneeKey ?? null,
            userOrUserTypeId,
            isUserType: Boolean(option?.isUserType),
        });
        setAssigningUserId("");
        if (response?.success || response?.isSuccess) {
            toast.success(response?.message || "Assigned successfully");
            await refreshAssignedItemsFromTask(taskForm?.id);
            setAssignedSearch("");
            return;
        }
        toast.error(response?.message || "Unable to assign");
    };

    const handleSendTaskComment = async () => {
        if (!taskForm?.id) {
            toast.error("Please save task first");
            return;
        }
        if (!String(taskComment || "").trim()) {
            toast.error("Comment text is required");
            return;
        }
        if (taskCommentSaving) return;

        const formData = new FormData();
        formData.append("TaskPlannerId", taskForm?.id || "");
        formData.append("CommentText", taskComment || "");
        if (taskImageFile instanceof File) {
            formData.append("ImageFile", taskImageFile);
        } else {
            formData.append("ImageFile", "");
        }

        setTaskCommentSaving(true);
        try {
            const response = await addTaskPlannerComment(formData);
            if (response?.success || response?.isSuccess) {
                toast.success(response?.message || "Comment sent");
                setTaskComment("");
                setTaskImageFile(null);
                await fetchTaskComments(taskForm?.id || "");
                return;
            }
            toast.error(response?.message || "Unable to send comment");
        } finally {
            setTaskCommentSaving(false);
        }
    };

    const fetchTaskStatusOptions = async () => {
        try {
            const response = await getTaskPlannerStatusOptions();
            const options = mapTaskPlannerSelectOptionsFromApi(response?.data ?? response);
            setTaskStatusOptions(options);
        } catch (error) {
            toast.error(error?.message || "Unable to load task status options");
            setTaskStatusOptions([]);
        }
    };

    const fetchTaskPriorityOptions = async () => {
        try {
            const response = await getTaskPlannerPriorityOptions();
            const options = mapTaskPlannerSelectOptionsFromApi(response?.data ?? response);
            setTaskPriorityOptions(options);
        } catch (error) {
            toast.error(error?.message || "Unable to load task priority options");
            setTaskPriorityOptions([]);
        }
    };

    useEffect(() => {
        fetchTaskPlannerTasks(buildTaskPlannerFilterPayload(taskPlannerFilter));
        fetchTaskStatusOptions();
        fetchTaskPriorityOptions();
        if(userData && userData?.userTypeId===1){
        fetchUserOptions();
        }
    }, []);

    useEffect(() => {
        const taskIdFromRoute = String(router?.params?.taskId || "").trim();
        if (taskIdFromRoute) {
            openTaskModal("edit", { id: taskIdFromRoute });
        }
    }, [router?.params?.taskId]);

    useEffect(() => {
        if (!taskModalOpen) return;
        const onClickOutside = (event) => {
            if (assignedPickerRef.current && !assignedPickerRef.current.contains(event.target)) {
                setAssignedPickerOpen(false);
            }
            if (
                commentMentionRef.current &&
                !commentMentionRef.current.contains(event.target)
            ) {
                resetCommentMentionState();
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => {
            document.removeEventListener("mousedown", onClickOutside);
        };
    }, [taskModalOpen]);

      useEffect(() => {
        const searchText = String(assignedSearch || "").trim();
        const taskPlannerId = String(taskForm?.id || "").trim();
        if (!taskModalOpen || !assignedPickerOpen || !searchText || !taskPlannerId) {
            setSearchUserOptions([]);
            return;
        }

        const timer = setTimeout(async () => {
            const response = await searchTaskPlannerAssigned({
                taskPlannerId,
                search: searchText,
                pageSize: 50
            });
            const mapped = mapAssignedSearchOptionsFromApi(response?.data ?? response);
            setSearchUserOptions(mapped);
        }, 300);

        return () => clearTimeout(timer);
    }, [assignedSearch, assignedPickerOpen, taskModalOpen, taskForm?.id]);

    const plannerMatrix = useMemo(() => {
        const buckets = (Array.isArray(plannerBuckets) ? plannerBuckets : []).map(
            (bucket, index) => ({
                ...bucket,
                id: bucket?.id || `bucket-${index}`,
            })
        );

        const matrixRows = (Array.isArray(plannerRows) ? plannerRows : []).map((row) => ({
            referenceId: row?.referenceId || "",
            cells: buckets.map((bucket) => {
                const cell = (row?.cells || []).find(
                    (item) => String(item?.bucketId ?? "") === String(bucket.id ?? "")
                );
                return {
                    bucketId: bucket.id,
                    task: cell?.task ? mapTaskFromApi(cell.task) : null,
                };
            }),
        }));

        return { buckets, matrixRows };
    }, [plannerBuckets, plannerRows]);

    const getBucketTaskCount = useCallback(
        (bucketId = "") =>
            plannerMatrix.matrixRows.reduce((count, row) => {
                const cell = row.cells.find(
                    (item) => String(item?.bucketId ?? "") === String(bucketId ?? "")
                );
                return count + (cell?.task ? 1 : 0);
            }, 0),
        [plannerMatrix.matrixRows]
    );

    const processes = useMemo(
        () =>
            plannerMatrix.buckets.map((bucket, index) => ({
                id: bucket?.id,
                label: bucket?.title || "",
                disabled: String(bucket?.status || "active").toLowerCase() === "deactive",
                active: index === 0,
            })),
        [plannerMatrix.buckets]
    );

    const visibleProcesses = useMemo(() => {
        if (!focusedBucketId) return processes;
        return processes.filter(
            (process) => String(process.id) === String(focusedBucketId)
        );
    }, [processes, focusedBucketId]);

    const boardGridColumns = focusedBucketId
        ? "minmax(360px, 420px)"
        : `repeat(${Math.max(visibleProcesses.length, 1)}, minmax(360px, 1fr))`;

    const assignedIdSet = useMemo(
        () => new Set((assignedItems || []).map((item) => String(item?.id))),
        [assignedItems]
    );

    const suggestionItems = useMemo(() => {
        const searchText = String(assignedSearch || "").trim();
        if (!searchText) return [];
        return (searchUserOptions || []).filter((item) => {
            const itemId = String(item?.value || "");
            return itemId && !assignedIdSet.has(itemId);
        });
    }, [assignedSearch, assignedIdSet, searchUserOptions]);

    const getCommentAvatarText = (userName = "") => {
        return String(userName || "").trim().charAt(0).toUpperCase() || "U";
    };

    const getNameInitials = (fullName = "") => {
        const parts = String(fullName || "")
            .trim()
            .split(/\s+/)
            .filter(Boolean);
        if (!parts.length) return "U";
        if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
        return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
    };

    const getCommentDateText = (value = "") => {
        if (!value) return "";
        const d = dayjs(value);
        if (!d.isValid()) return String(value);
        return d.format("h:mm A MM-DD-YYYY");
    };

    return (
        <div className="page-content task-planner">
            <Container fluid>

                {/* HEADER */}
                <div className="task-planner-header d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Task Planner</h5>

                    <div className="task-planner-toolbar d-flex align-items-center gap-2">
                        <div className="task-planner-filter-actions d-flex align-items-center gap-2">
                            <Button
                                color="primary"
                                type="button"
                                onClick={() => setCommonFilterModalOpen(true)}
                                className="task-planner-toolbar-btn position-relative"
                                aria-label={
                                    activeTaskPlannerFilterCount > 0
                                        ? `Open filters, ${activeTaskPlannerFilterCount} filter${
                                              activeTaskPlannerFilterCount === 1 ? "" : "s"
                                          } applied`
                                        : "Open filters"
                                }
                            >
                                {activeTaskPlannerFilterCount > 0 ? (
                                    <span className="table-filter-btn-live-dot" aria-hidden="true">
                                        {activeTaskPlannerFilterCount}
                                    </span>
                                ) : null}
                                <i className="fa fa-filter me-1" aria-hidden="true" />
                                Filter
                            </Button>
                            <Button
                                color="light"
                                type="button"
                                className="task-planner-toolbar-btn border task-planner-reset-btn"
                                onClick={handleTaskPlannerFilterReset}
                            >
                                <i className="fa fa-undo me-1" aria-hidden="true" />
                                Reset
                            </Button>
                        </div>
                        <div className="task-planner-scroll-controls d-flex gap-2">
                            <span className="scroll-icon" onClick={scrollLeft}>
                                <i className="fa fa-chevron-left" />
                            </span>
                            <span className="scroll-icon" onClick={scrollRight}>
                                <i className="fa fa-chevron-right" />
                            </span>
                        </div>
                    </div>
                </div>

                {/* GRID */}
                {tasksLoading ? (
                    <Loaders />
                ) : (
                <div
                    className={classNames("process-grid-scroll", {
                        "is-bucket-focused": Boolean(focusedBucketId),
                    })}
                    ref={gridRef}
                >
                    <div className="planner-board">
                    <div
                        className="planner-board-header"
                        style={{ gridTemplateColumns: boardGridColumns }}
                    >
                        {visibleProcesses.map((process) => {
                            const isFocusedBucket =
                                String(focusedBucketId) === String(process.id);
                            return (
                                <div
                                    key={process.id}
                                    className={classNames("process-item", {
                                        active: focusedBucketId
                                            ? isFocusedBucket
                                            : process.active,
                                    })}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleBucketHeaderClick(process.id)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            handleBucketHeaderClick(process.id);
                                        }
                                    }}
                                >
                                    <span>{process.label}</span>
                                    <span
                                        className={classNames("process-count", {
                                            active: focusedBucketId
                                                ? isFocusedBucket
                                                : process.active,
                                        })}
                                    >
                                        {getBucketTaskCount(process.id)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    <div
                        className={classNames("planner-board-body", {
                            "process-column-scroll": Boolean(focusedBucketId),
                        })}
                    >
                        {plannerMatrix.matrixRows.map((matrixRow, rowIdx) => (
                            <div
                                key={matrixRow.referenceId || `planner-row-${rowIdx}`}
                                className="planner-board-row"
                                style={{ gridTemplateColumns: boardGridColumns }}
                            >
                                {visibleProcesses.map((process) => {
                                    const cell = matrixRow.cells.find(
                                        (item) =>
                                            String(item?.bucketId ?? "") ===
                                            String(process.id ?? "")
                                    );
                                    const task = cell?.task;

                                    return (
                                        <div
                                            key={`${process.id}-${matrixRow.referenceId || rowIdx}`}
                                            className="planner-board-cell"
                                        >
                                            {task ? (
                                                <Card
                                                    className={classNames("task-card", {
                                                        "task-card-disabled": process.disabled,
                                                    })}
                                                    role="button"
                                                    tabIndex={process.disabled ? -1 : 0}
                                                    onClick={() => {
                                                        if (!process.disabled) {
                                                            openTaskModal("edit", task);
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (process.disabled) return;
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            openTaskModal("edit", task);
                                                        }
                                                    }}
                                                >
                                                    <CardBody>
                                                        <div className="d-flex justify-content-between">
                                                            <h6 className="task-title">{task.title}</h6>
                                                            <div
                                                                className="task-card-actions"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <UncontrolledDropdown>
                                                                    <DropdownToggle
                                                                        tag="span"
                                                                        className="text-muted cursor-pointer"
                                                                    >
                                                                        •••
                                                                    </DropdownToggle>
                                                                    <DropdownMenu end>
                                                                        {task?.referenceId ? (
                                                                            <DropdownItem
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleReferenceNavigate(task);
                                                                                }}
                                                                            >
                                                                                <i className="fa fa-link me-2"></i>
                                                                                Reference
                                                                            </DropdownItem>
                                                                        ) : null}
                                                                        <DropdownItem
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                openTaskModal("edit", task);
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-pencil-alt me-2"></i>
                                                                            Edit
                                                                        </DropdownItem>
                                                                        <DropdownItem
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleTaskDelete(task);
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-trash me-2"></i>
                                                                            Delete
                                                                        </DropdownItem>
                                                                    </DropdownMenu>
                                                                </UncontrolledDropdown>
                                                            </div>
                                                        </div>

                                                        <div className="d-flex gap-2 mt-3">
                                                        <span className={`pill pill-priority ${task?.priority=='high' ? 'pill-priority-high' : ''}`} >
                                                        {String(task?.priority || "medium")
                                                                    .charAt(0)
                                                                    .toUpperCase() +
                                                                    String(task?.priority || "medium").slice(1)}
                                                            </span>
                                                            <span
                                                                className={classNames("pill", {
                                                                    "pill-pending": [
                                                                        "approval pending",
                                                                        "not started",
                                                                    ].includes(
                                                                        String(task.status || "").toLowerCase()
                                                                    ),
                                                                    "pill-in-progress": [
                                                                        "in progress"
                                                                    ].includes(
                                                                        String(task.status || "").toLowerCase()
                                                                    ),
                                                                    "pill-success": [
                                                                        "success",
                                                                        "completed",
                                                                        "done",
                                                                    ].includes(
                                                                        String(task.status || "").toLowerCase()
                                                                    ),
                                                                })}
                                                            >
                                                                {task.status}
                                                            </span>
                                                        </div>

                                                        <div className="date-box mt-4 fw-300">
                                                            <div className="mt-2">
                                                                <i className="fa fa-calendar" /> Start:{" "}
                                                                {task.start}
                                                            </div>
                                                            <div className="mt-2">
                                                                <i className="fa fa-calendar" /> Due:{" "}
                                                                {task.due || "-"}
                                                            </div>
                                                        </div>

                                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                                            <span className="text-muted task-card-description-preview">
                                                                {getTaskCardDescriptionPreview(
                                                                    task?.description
                                                                )}
                                                            </span>
                                                            <div className="d-flex align-items-center gap-1 text-muted">
                                                                <i className="fa fa-comment" />
                                                                <span>{task.comments}</span>
                                                            </div>
                                                        </div>

                                                        <div className="task-footer d-flex justify-content-between align-items-center mt-4 text-muted">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="assigned-users">
                                                                    <div className="assigned-avatars">
                                                                        {(task?.otherAssignees || [])
                                                                            .slice(0, 1)
                                                                            .map((assignee) => (
                                                                                task?.assignedToName ?
                                                                                <span
                                                                                    key={
                                                                                        assignee?.id ||
                                                                                        assignee?.name
                                                                                    }
                                                                                    className="assigned-avatar"
                                                                                    title={
                                                                                        assignee?.name ||
                                                                                        "Assignee"
                                                                                    }
                                                                                >
                                                                                    {assignee?.imageUrl ? (
                                                                                        <img
                                                                                            src={
                                                                                                assignee.imageUrl
                                                                                            }
                                                                                            alt={
                                                                                                assignee?.name ||
                                                                                                "Assignee"
                                                                                            }
                                                                                            className="assigned-avatar-img"
                                                                                        />
                                                                                    ) : (
                                                                                        <i
                                                                                            className="fa fa-user"
                                                                                            aria-hidden="true"
                                                                                        />
                                                                                    )}
                                                                                </span>
                                                                                : null
                                                                            ))}
                                                                    </div>
                                                                </div>
                                                                {task?.assignedToName ?
                                                                <span className="owner-name">
                                                                    {task?.assignedToName || "—"}
                                                                </span> : null
                                }
                                                            </div>

                                                            <div className="assigned-users">
                                                                <div className="assigned-avatars">
                                                                    {(task?.otherAssignees || [])
                                                                        .slice(0, 3)
                                                                        .map((assignee) => (
                                                                            <span
                                                                                key={`group-${assignee?.id || assignee?.name}`}
                                                                                className="assigned-avatar"
                                                                                title={
                                                                                    assignee?.name ||
                                                                                    "Assignee"
                                                                                }
                                                                            >
                                                                                {assignee?.imageUrl ? (
                                                                                    <img
                                                                                        src={assignee.imageUrl}
                                                                                        alt={
                                                                                            assignee?.name ||
                                                                                            "Assignee"
                                                                                        }
                                                                                        className="assigned-avatar-img"
                                                                                    />
                                                                                ) : (
                                                                                    <i
                                                                                        className="fa fa-user"
                                                                                        aria-hidden="true"
                                                                                    />
                                                                                )}
                                                                            </span>
                                                                        ))}
                                                                </div>
                                                                <span className="assigned-text">
                                                                    {Number(
                                                                        task?.otherAssigneeCount || 0
                                                                    )}{" "}
                                                                    assigned
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            ) : (
                                                <div
                                                    className="task-card-empty-slot"
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                    </div>
                </div>
                )}

                <Modal
                    isOpen={taskModalOpen}
                    toggle={closeTaskModal}
                    backdrop="static"
                    zIndex={5000}
                    className={classNames("modal-full-height task-planner-add-edit-modal", {
                        "is-assigned-picker-open": assignedPickerOpen,
                    })}
                    size="xl"
                    centered
                >
                    <ModalBody className="p-4 task-planner-modal-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-bold mb-0 text-uppercase">
                                Task
                            </h5>
                            <Button close onClick={closeTaskModal} />
                        </div>
                        <hr />

                        {!taskModalLoading ? (
                            <Form onSubmit={handleTaskSave} className="task-modal-form">
                                <Row className="task-modal-form-row g-3">
                                    <Col lg={5} className="task-modal-details-col">
                                        <div className="task-section-card">
                                            <div className="task-section-title">Task Assignment & Update</div>
                                            <div className="task-section-divider"></div>

                                            <FormGroup>
                                                <Label>
                                                    Assigned
                                                </Label>
                                                <div className="task-assigned-picker" ref={assignedPickerRef}>
                                                    <div
                                                        className={classNames("task-assigned-avatars", {
                                                            disabled: taskModalMode === "view",
                                                        })}
                                                    >
                                                        {(assignedItems || []).slice(0, 5).map((item) => (
                                                            <span
                                                                key={item?.assignmentId || item?.id}
                                                                className="task-assigned-avatar-item"
                                                                title={item?.label || "Assigned user"}
                                                            >
                                                                {getNameInitials(item?.label)}
                                                            </span>
                                                        ))}
                                                        {taskModalMode !== "view" ? (
                                                            <span
                                                                className="task-assigned-avatar-item add"
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setAssignedPickerOpen((prev) => !prev);
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter" || e.key === " ") {
                                                                        e.preventDefault();
                                                                        setAssignedPickerOpen((prev) => !prev);
                                                                    }
                                                                }}
                                                            >
                                                                <i className="fa fa-user-plus"></i>
                                                            </span>
                                                        ) : null}
                                                    </div>

                                                    {assignedPickerOpen && taskModalMode !== "view" ? (
                                                        <div className="task-assigned-panel">
                                                            <Input
                                                                type="text"
                                                                placeholder="Type User Type Or Name."
                                                                value={assignedSearch}
                                                                onChange={(e) => setAssignedSearch(e?.target?.value || "")}
                                                            />

                                                            {!String(assignedSearch || "").trim() ? (
                                                                <div className="task-assigned-section mt-3">
                                                                    <h6>Assigned</h6>
                                                                    {(assignedItems || []).length ? (
                                                                        (assignedItems || []).map((item) => (
                                                                            <div
                                                                                key={item?.assignmentId || item?.id}
                                                                                className="task-assigned-row"
                                                                            >
                                                                                <div className="left">
                                                                                    <i className="fa fa-user"></i>
                                                                                    <span>{item?.label}</span>
                                                                                </div>
                                                                                <button
                                                                                    type="button"
                                                                                    className="icon-btn"
                                                                                    onClick={() => handleRemoveAssigned(item)}
                                                                                >
                                                                                    <i className="fa fa-times text-danger"></i>
                                                                                </button>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <small className="text-muted">No assigned users</small>
                                                                    )}
                                                                </div>
                                                            ) : null}

                                                            {String(assignedSearch || "").trim() ? (
                                                                <div className="task-assigned-section mt-3">
                                                                    <h6>Suggestions</h6>
                                                                    {(suggestionItems || []).length ? (
                                                                        (suggestionItems || []).map((item) => (
                                                                            <div key={item?.value} className="task-assigned-row">
                                                                                <div className="left">
                                                                                    <i className="fa fa-user"></i>
                                                                                    <span>{item?.label}</span>
                                                                                </div>
                                                                                <button
                                                                                    type="button"
                                                                                    className="icon-btn"
                                                                                    disabled={assigningUserId === item?.value}
                                                                                    onClick={() => handleAssignUser(item)}
                                                                                >
                                                                                    <i
                                                                                        className={classNames("fa", {
                                                                                            "fa-spinner fa-spin":
                                                                                                assigningUserId === item?.value,
                                                                                            "fa-plus-square":
                                                                                                assigningUserId !== item?.value,
                                                                                        })}
                                                                                    ></i>
                                                                                </button>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <small className="text-muted">No suggestions found</small>
                                                                    )}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </FormGroup>

                                            <FormGroup>
                                                <Label>
                                                    Title <span className="text-danger">*</span>
                                                </Label>
                                                <Input
                                                    type="text"
                                                    name="title"
                                                    value={taskForm?.title || ""}
                                                    onChange={handleTaskFormChange}
                                                    disabled={taskModalMode === "view"}
                                                />
                                            </FormGroup>

                                            <Row>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label>
                                                            Bucket <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="select"
                                                            name="taskBucketId"
                                                            value={taskForm?.taskBucketId || ""}
                                                            onChange={handleTaskFormChange}
                                                            disabled={true}
                                                        >
                                                            <option value="">Select Bucket</option>
                                                            {processes.map((process) => (
                                                                <option key={process?.id} value={process?.id}>
                                                                    {process?.label}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label>
                                                            Status <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="select"
                                                            name="status"
                                                            value={taskForm?.status || ""}
                                                            onChange={handleTaskFormChange}
                                                            disabled={taskModalMode === "view"}
                                                        >
                                                            <option value="">Select Status</option>
                                                            {taskStatusOptions.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label>
                                                            Priority <span className="text-danger">*</span>
                                                        </Label>
                                                        <Input
                                                            type="select"
                                                            name="priority"
                                                            value={taskForm?.priority || ""}
                                                            onChange={handleTaskFormChange}
                                                            disabled={taskModalMode === "view"}
                                                        >
                                                            <option value="">Select Priority</option>
                                                            {taskPriorityOptions.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <FormGroup>
                                                <Label>Notes</Label>
                                                <Input
                                                    type="textarea"
                                                    rows={3}
                                                    name="description"
                                                    value={taskForm?.description || ""}
                                                    onChange={handleTaskFormChange}
                                                    disabled={taskModalMode === "view"}
                                                />
                                            </FormGroup>

                                            <FormGroup>
                                                <Label>Due Date</Label>
                                                <Input
                                                    type="date"
                                                    name="dueDate"
                                                    value={taskForm?.dueDate || ""}
                                                    onChange={handleTaskFormChange}
                                                    disabled={taskModalMode === "view"}
                                                />
                                            </FormGroup>

                                            {taskModalMode !== "view" ? (
                                                <div className="text-end mt-2">
                                                    <Button color="primary" type="submit">
                                                        {/* <i className="bx bx-save me-1"></i> */}
                                                        {taskModalMode === "edit" ? "Update" : "Save"}
                                                    </Button>
                                                </div>
                                            ) : null}
                                        </div>
                                    </Col>

                                    <Col lg={7} className="task-modal-comments-col">
                                        <div className="task-section-card task-comment-send-card">
                                            <div className="task-section-title">Comment Send</div>
                                            <div className="task-section-divider"></div>

                                            <div className="task-comment-send-row">
                                                <div className="task-comment-send-col">
                                                    <FormGroup className="mb-0 task-comment-send-field">
                                                        <Label>Comments</Label>
                                                        <div
                                                            className="task-comment-mention-wrap"
                                                            ref={commentMentionRef}
                                                        >
                                                            <div
                                                                className="task-comment-mention-field"
                                                                onClick={focusTaskCommentInput}
                                                            >
                                                                <div
                                                                    ref={taskCommentBackdropRef}
                                                                    className="task-comment-mention-backdrop form-control"
                                                                    aria-hidden="true"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html:
                                                                            formatTaskCommentMentionHtml(
                                                                                taskComment
                                                                            ) || "&nbsp;",
                                                                    }}
                                                                />
                                                                <textarea
                                                                    ref={taskCommentInputRef}
                                                                    className="form-control task-comment-mention-input"
                                                                    rows={2}
                                                                    value={taskComment}
                                                                    onChange={handleTaskCommentChange}
                                                                    onKeyDown={handleTaskCommentKeyDown}
                                                                    onScroll={syncTaskCommentBackdropScroll}
                                                                    disabled={taskModalMode === "view"}
                                                                    placeholder="Write a comment..."
                                                                />
                                                            </div>
                                                            {commentMentionOpen &&
                                                            taskModalMode !== "view" ? (
                                                                <div className="task-comment-mention-panel">
                                                                    {commentMentionLoading ? (
                                                                        <div className="task-comment-mention-empty">
                                                                            Loading assigned users...
                                                                        </div>
                                                                    ) : filteredCommentMentionOptions.length ? (
                                                                        filteredCommentMentionOptions.map(
                                                                            (mentionOption, mentionIndex) => (
                                                                                <button
                                                                                    key={
                                                                                        mentionOption?.value ||
                                                                                        mentionOption?.label
                                                                                    }
                                                                                    type="button"
                                                                                    className={classNames(
                                                                                        "task-comment-mention-item",
                                                                                        {
                                                                                            active:
                                                                                                mentionIndex ===
                                                                                                commentMentionHighlightIndex,
                                                                                        }
                                                                                    )}
                                                                                    onMouseDown={(event) => {
                                                                                        event.preventDefault();
                                                                                        insertCommentMention(
                                                                                            mentionOption
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    {mentionOption?.label}
                                                                                </button>
                                                                            )
                                                                        )
                                                                    ) : (
                                                                        <div className="task-comment-mention-empty">
                                                                            No assigned users found
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </FormGroup>
                                                </div>

                                                <div className="task-comment-send-col">
                                                    <FormGroup className="mb-0 task-comment-send-field">
                                                        <Label>Image (jpg,jpeg,png)</Label>
                                                        <div className="task-comment-file-wrap">
                                                            <label
                                                                className={classNames(
                                                                    "task-comment-file-picker",
                                                                    {
                                                                        disabled: taskModalMode === "view",
                                                                        "has-file": Boolean(taskImageFile),
                                                                    }
                                                                )}
                                                            >
                                                                <input
                                                                    type="file"
                                                                    className="task-comment-file-input"
                                                                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                                                    onChange={(e) =>
                                                                        setTaskImageFile(
                                                                            e?.target?.files?.[0] || null
                                                                        )
                                                                    }
                                                                    disabled={taskModalMode === "view"}
                                                                />
                                                                <i
                                                                    className="fa fa-image"
                                                                    aria-hidden="true"
                                                                />
                                                                <span className="task-comment-file-picker-text">
                                                                    {taskImageFile?.name || "Choose file"}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    </FormGroup>
                                                </div>
                                            </div>

                                            {taskModalMode !== "view" ? (
                                                <div className="text-end mt-1">
                                                    <Button
                                                        type="button"
                                                        color="primary"
                                                        className="text-white"
                                                        disabled={taskCommentSaving}
                                                        onClick={handleSendTaskComment}
                                                    >
                                                        {taskCommentSaving ? (
                                                            <>
                                                                <i
                                                                    className="fa fa-spinner fa-spin me-1"
                                                                    aria-hidden="true"
                                                                />
                                                                Saving...
                                                            </>
                                                        ) : (
                                                            "Save"
                                                        )}
                                                    </Button>
                                                </div>
                                            ) : null}
                                        </div>

                                        <div className="task-section-card task-comment-view-card mt-2">
                                            <div className="task-section-title">Comment View</div>
                                            <div className="task-section-divider"></div>

                                            <div className="task-comment-list mt-2">
                                                {taskCommentsLoading ? (
                                                    <Loaders />
                                                ) : (taskComments || []).length ? (
                                                    (taskComments || []).map((commentItem) => {
                                                        const hasText = Boolean(
                                                            String(commentItem?.commentText || "").trim()
                                                        );
                                                        const hasImage = Boolean(commentItem?.imageUrl);

                                                        return (
                                                            <div
                                                                key={commentItem?.id}
                                                                className="task-comment-card"
                                                            >
                                                                <div className="task-comment-head">
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <span className="task-comment-avatar">
                                                                            {getCommentAvatarText(
                                                                                commentItem?.userName
                                                                            )}
                                                                        </span>
                                                                        <span className="task-comment-name">
                                                                            {commentItem?.userName}
                                                                        </span>
                                                                    </div>
                                                                    <span className="task-comment-date">
                                                                        {getCommentDateText(
                                                                            commentItem?.createdOn
                                                                        )}
                                                                    </span>
                                                                </div>

                                                                <div
                                                                    className={classNames(
                                                                        "task-comment-body",
                                                                        {
                                                                            "has-image": hasImage,
                                                                            "has-text": hasText,
                                                                        }
                                                                    )}
                                                                >
                                                                    {hasText ? (
                                                                        <div
                                                                            className="task-comment-text"
                                                                            onClick={(event) =>
                                                                                handleCommentTextImageActivate(
                                                                                    event,
                                                                                    commentItem
                                                                                )
                                                                            }
                                                                            onKeyDown={(event) => {
                                                                                if (
                                                                                    event.key === "Enter" ||
                                                                                    event.key === " "
                                                                                ) {
                                                                                    handleCommentTextImageActivate(
                                                                                        event,
                                                                                        commentItem
                                                                                    );
                                                                                }
                                                                            }}
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: formatTaskCommentMentionDisplayHtml(
                                                                                    commentItem.commentText
                                                                                ),
                                                                            }}
                                                                        />
                                                                    ) : null}

                                                                    {hasImage ? (
                                                                        <div className="task-comment-image-wrap">
                                                                            <img
                                                                                src={commentItem?.imageUrl}
                                                                                alt="Comment"
                                                                                className="task-comment-image"
                                                                                role="button"
                                                                                tabIndex={0}
                                                                                onClick={() =>
                                                                                    openImagePreview(
                                                                                        commentItem?.imageUrl,
                                                                                        `Comment by ${commentItem?.userName || "User"}`
                                                                                    )
                                                                                }
                                                                                onKeyDown={(event) => {
                                                                                    if (
                                                                                        event.key === "Enter" ||
                                                                                        event.key === " "
                                                                                    ) {
                                                                                        event.preventDefault();
                                                                                        openImagePreview(
                                                                                            commentItem?.imageUrl,
                                                                                            `Comment by ${commentItem?.userName || "User"}`
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="text-muted">No comments found</div>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        ) : (
                            <Loaders />
                        )}
                    </ModalBody>
                </Modal>

                <AllCommonFilterModal
                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                    commonFilterModalOpen={commonFilterModalOpen}
                    modalTitle="Task Planner Filter"
                    handleSubmit={handleTaskPlannerFilterSubmit}
                    filterData={taskPlannerFilterModalData}
                />

                <Modal
                    isOpen={imagePreviewModalOpen}
                    toggle={closeImagePreview}
                    backdrop="static"
                    zIndex={5100}
                    className="modal-full-height image-preview-modal"
                    centered
                    size="lg"
                >
                    <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-bold mb-0">Preview Image</h5>
                            <Button close onClick={closeImagePreview} />
                        </div>
                        <hr />
                        <div className="text-center">
                            <img
                                src={previewImageUrl}
                                alt={previewImageAlt}
                                className="client-review-preview-image"
                            />
                            <div className="d-flex justify-content-center mt-2">
                                <ReviewImageDownloadLink href={previewImageUrl} />
                            </div>
                        </div>
                    </ModalBody>
                </Modal>

            </Container>
        </div>
    );
};

export default withRouter(TaskPlanner);
